import i18n from "@/i18n";
/**
 * VuosilomaBookingFields — VUOSILOMA-V1. ANNUAL_LEAVE-only booking
 * extension inside the absence form: server preview of counted holiday
 * days, holiday-year selection (oldest known balance pre-selected),
 * employer confirmation. Never blocks recording on balance alone.
 */
import { useEffect, useState } from "react";
import { Checkbox } from "@/components/ui/checkbox";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { api } from "@/lib/api";
import { useLatest } from "@/hooks/useLatest";
import { creditYearLabel, vlDays, VL_WARNING_TEXT } from "./vuosilomaHelpers";

const NONE = "__none__";

export default function VuosilomaBookingFields({
    employeeId, startDate, endDate, excludeAbsenceId, value, onChange,
}) {
    const [preview, setPreview] = useState(null);
    const [touched, setTouched] = useState(!!value.funding_year);
    const latest = useLatest({ touched, value, onChange });

    useEffect(() => {
        if (!employeeId || !startDate || !endDate || startDate > endDate) {
            setPreview(null);
            return undefined;
        }
        let alive = true;
        Promise.resolve(api.post(`/workforce/employees/${employeeId}/vuosiloma/booking-preview`, {
            start_date: startDate, end_date: endDate,
            funding_year: value.funding_year || null,
            exclude_absence_id: excludeAbsenceId || null,
        })).then((res) => {
            const data = res?.data;
            if (!alive || !data) return;
            setPreview(data);
            const cur = latest.current;
            if (!cur.touched && data.suggested_funding_year && !cur.value.funding_year) {
                cur.onChange({ ...cur.value, funding_year: data.suggested_funding_year });
            }
        }).catch(() => alive && setPreview(null));
        return () => { alive = false; };
    }, [employeeId, startDate, endDate, value.funding_year, excludeAbsenceId, latest]);

    const years = preview?.credit_years || [];
    return (
        <div className="space-y-3 rounded-md border border-sky-200 bg-sky-50/60 p-3 text-sm"
            data-testid="vl-booking-fields">
            <div data-testid="vl-booking-counted">
                {preview
                    ? preview.counted_days === null
                        ? i18n.t("wf.vuosilomaBookingFields.holidayDaysCannotBeCountedThe", "Holiday days cannot be counted: the holiday calendar does not cover these dates.")
                        : `${vlDays(preview.counted_days)} counted as holiday days (Sundays and church holidays excluded)`
                    : i18n.t("wf.vuosilomaBookingFields.chooseDatesToPreviewHolidayDays", "Choose dates to preview holiday days.")}
            </div>
            <div>
                <label className="text-xs text-neutral-600">{i18n.t("wf.vuosilomaBookingFields.fundedFromHolidayYear", "Funded from holiday year")}</label>
                <Select value={value.funding_year || NONE}
                    onValueChange={(v) => { setTouched(true); onChange({ ...value, funding_year: v === NONE ? null : v }); }}>
                    <SelectTrigger data-testid="vl-booking-year"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value={NONE} data-testid="vl-booking-year-none">{i18n.t("wf.vuosilomaBookingFields.notSelectedYet", "Not selected yet")}</SelectItem>
                        {years.map((y) => (
                            <SelectItem key={y.credit_year_start} value={y.credit_year_start}
                                data-testid={`vl-booking-year-${y.credit_year_start}`}>
                                {creditYearLabel(y.credit_year_start)} · available {y.available_days ?? "not known"}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                {preview?.selected_funding_year && (
                    <p className="mt-1 text-xs text-neutral-600" data-testid="vl-booking-balance">
                        Available before {vlDays(preview.selected_available_before)}
                        {" · after this booking "}{vlDays(preview.selected_available_after)}
                        {preview.suggested_funding_year === preview.selected_funding_year
                            ? " · oldest known balance pre-selected" : ""}
                    </p>
                )}
            </div>
            <label className="flex items-center gap-2">
                <Checkbox checked={!!value.employer_confirmed} data-testid="vl-booking-confirmed"
                    onCheckedChange={(c) => onChange({ ...value, employer_confirmed: !!c })} />
                <span>{i18n.t("wf.vuosilomaBookingFields.employerHasConfirmedTheseHolidayDates", "Employer has confirmed these holiday dates")}</span>
            </label>
            {(preview?.warnings || []).map((w) => (
                <p key={w} className="text-xs text-amber-800" data-testid={`vl-booking-warning-${w}`}>
                    {VL_WARNING_TEXT[w] || w}
                </p>
            ))}
        </div>
    );
}
